import { gql, useApolloClient } from "@apollo/client";
import {
  AppSessionCellId,
  CellExecutionState,
  MAX_NON_METADATA_OUTPUT_COUNT,
  OUTPUTS_TRUNCATED_MIMETYPE,
  OutputContentId,
  OutputId,
  OutputType,
  OutputsTruncatedContents,
  getDateTimeString,
  stableEmptyArray,
  uuid,
} from "@hex/common";
import { max } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { shallowEqual } from "../../util/index.js";
import { useDebounce } from "use-debounce";

import { useAppSessionCellSelector } from "../../appsession-multiplayer/state-hooks/appSessionCellStateHooks";
import { useDispatch, useSelector, useStore } from "../../redux/hooks.js";
import {
  AppSessionCellMP,
  OutputMPWithResolvedContent,
  appSessionMPSelectors,
} from "../../redux/slices/appSessionMPSlice";
import { hexVersionMPSelectors } from "../../redux/slices/hexVersionMPSlice";
import {
  OutputContentLoadStatus,
  outputContentActions,
} from "../../redux/slices/outputContentSlice";
import { useProjectContext } from "../../util/projectContext";
import { useSessionContext } from "../../util/sessionContext";
import { ElementType } from "../elements/ElementType";

import { parseOutputType } from "./parseOutputType";
import {
  OutputContentByIdDocument,
  OutputContentByIdQuery,
  OutputContentByIdQueryVariables,
} from "./useCellData.generated";

gql`
  query OutputContentById($contentIds: [OutputContentId!]!) {
    safeOutputContentById(contentIds: $contentIds) {
      ... on OutputContent {
        id
        createdDate
        updatedDate
        deletedDate
        revision
        mimeType
        contents
        requiresLazyLoad
      }
      ... on OutputContentNotFound {
        id
      }
    }
  }
`;

const DEBOUNCE_OUTPUT_LOADING_STATE_MS = 250;
const DEBOUNCE_RENDER_OUTPUT_STATE_MS = 100;

export interface UseCellDataResult {
  key: string;
  outputFragment: OutputMPWithResolvedContent;
  type: ElementType | null;
}

export interface UseCellDataArgs {
  appSessionCellId: AppSessionCellId | undefined;
  filterErrors?: boolean;
  skipDebounces?: boolean;
}

export const useCellLastExecutionTime = (
  appSessionCellId: AppSessionCellId | undefined,
): {
  lastExecutionStartTime: Date | null;
  lastExecutionEndTime: Date | null;
} => {
  const appSessionCell = useAppSessionCellSelector({
    appSessionCellId,
    selector: (cell) => {
      return {
        lastExecutionEndTime: cell?.lastExecutionEndTime,
        lastExecutionStartTime: cell?.lastExecutionStartTime,
      };
    },
    safe: true,
    equalityFn: shallowEqual,
  });

  const lastExecutionStartTimeStr =
    appSessionCell?.lastExecutionStartTime ?? null;
  const lastExecutionEndTimeStr = appSessionCell?.lastExecutionEndTime ?? null;

  return useMemo(
    () => ({
      lastExecutionStartTime:
        lastExecutionStartTimeStr != null
          ? new Date(lastExecutionStartTimeStr)
          : null,
      lastExecutionEndTime:
        lastExecutionEndTimeStr != null
          ? new Date(lastExecutionEndTimeStr)
          : null,
    }),
    [lastExecutionStartTimeStr, lastExecutionEndTimeStr],
  );
};

export function useLoadOutputContentsById() {
  const { appSessionId } = useSessionContext();
  const dispatch = useDispatch();
  const apolloClient = useApolloClient();

  return useCallback(
    async (ids: OutputContentId[]) => {
      if (ids.length === 0) return;

      // mark outputs as LOADING
      dispatch(
        outputContentActions.setValues({
          appSessionId,
          values: ids.map((id) => ({
            id,
            status: OutputContentLoadStatus.LOADING,
            contents: undefined,
          })),
        }),
      );

      try {
        // query the output content data
        const { data, errors } = await apolloClient.query<
          OutputContentByIdQuery,
          OutputContentByIdQueryVariables
        >({
          query: OutputContentByIdDocument,
          fetchPolicy: "network-only",
          variables: { contentIds: ids },
        });

        if (errors) {
          // if any errors reported, mark all outputs as FAILED
          console.error("Failed to load cell data", errors);
          dispatch(
            outputContentActions.setValues({
              appSessionId,
              values: ids.map((id) => ({
                id,
                status: OutputContentLoadStatus.FAILED,
                contents: undefined,
              })),
            }),
          );
        } else {
          const outputsNotFound = data.safeOutputContentById.filter(
            (content) => content.__typename === "OutputContentNotFound",
          );

          if (outputsNotFound.length > 0) {
            console.warn(
              "Output contents not found. Marked as failing so we don't endlessly retry.",
              outputsNotFound.map((o) => o.id),
            );
            dispatch(
              outputContentActions.setValues({
                appSessionId,
                values: outputsNotFound.map((content) => ({
                  id: content.id,
                  status: OutputContentLoadStatus.FAILED,
                  contents: undefined,
                })),
              }),
            );
          }

          const outputs = data.safeOutputContentById.filter(
            (content) => content.__typename === "OutputContent",
          );

          const outputsRequiringLazyLoad = outputs.filter(
            (content) => content.requiresLazyLoad,
          );
          if (outputsRequiringLazyLoad.length > 0) {
            // if any outputs require addl loading mark as FAILED
            console.warn(
              "Output contents are marked as requiring additional loading. Marked as failing so we don't endlessly retry.",
              outputsRequiringLazyLoad.map((o) => o.id),
            );
            dispatch(
              outputContentActions.setValues({
                appSessionId,
                values: outputsRequiringLazyLoad.map((content) => ({
                  id: content.id,
                  status: OutputContentLoadStatus.FAILED,
                  contents: undefined,
                })),
              }),
            );
          }

          const successfulOutputs = outputs.filter(
            (content) => !content.requiresLazyLoad,
          );
          if (successfulOutputs.length > 0) {
            // mark successfully loaded outputs as READY
            dispatch(
              outputContentActions.setValues({
                appSessionId,
                values: successfulOutputs.map((content) => ({
                  id: content.id,
                  status: OutputContentLoadStatus.READY,
                  contents: content.contents,
                })),
              }),
            );
          }
        }
      } catch (err) {
        // TODO(FOUND-1194): We might still hit this in situations where an output is added
        // and then immediately deleted. Easiest way to fix this is probably to just stop
        // output contents from being cleaned up immediately on delete, instead tombstone-ing
        // them for deletion at a later date.
        console.error("Failed to load cell data", err);
        dispatch(
          outputContentActions.setValues({
            appSessionId,
            values: ids.map((id) => ({
              id,
              status: OutputContentLoadStatus.FAILED,
              contents: undefined,
            })),
          }),
        );
      }
    },
    [apolloClient, appSessionId, dispatch],
  );
}

export const useEnsureOutputLoaded = (): {
  kickoff: (outputIds: OutputId[]) => void;
} => {
  const { appSessionId } = useSessionContext();
  const store = useStore();
  const loadOutputContentsById = useLoadOutputContentsById();

  return useMemo(() => {
    return {
      kickoff: (outputIds: OutputId[]) => {
        if (outputIds.length === 0) {
          return;
        }
        const state = store.getState();

        const unloadedOutputs =
          appSessionMPSelectors
            .getOutputSelectors(appSessionId)
            .selectAllUnloadedWithResolvedContent(state) ?? [];

        const idSet = new Set(outputIds);
        const relevantContents = unloadedOutputs
          .filter((o) => idSet.has(o.id))
          .flatMap((o) => o.frontendOutputContents)
          .filter((c) => c.status === OutputContentLoadStatus.NO_CONTENT)
          .map((c) => c.id);
        if (relevantContents.length === 0) return;
        void loadOutputContentsById(relevantContents);
      },
    };
  }, [store, appSessionId, loadOutputContentsById]);
};

export const useHydrateAllOutputs = (): void => {
  const { appSessionId } = useSessionContext();
  const { hexVersionId } = useProjectContext();
  const { kickoff } = useEnsureOutputLoaded();
  const unhydratedOutputs = useSelector(
    (state) => {
      // Right now events can sometimes load output ids that
      // are not in the app, can remove once FOUND-1311 is done
      const cellIds = new Set(
        hexVersionMPSelectors.getCellSelectors(hexVersionId).selectIds(state),
      );
      const appSessionCells =
        appSessionMPSelectors
          .getAppSessionCellSelectors(appSessionId)
          .selectAll(state) ?? [];

      const appSessionCellIds = new Set(
        appSessionCells.filter((a) => cellIds.has(a.cellId)).map((a) => a.id),
      );

      return (
        appSessionMPSelectors
          .getOutputSelectors(appSessionId)
          .selectAllUnloadedWithResolvedContent(state)
          .filter((o) => appSessionCellIds.has(o.appSessionCellId)) ?? []
      );
    },
    { equalityFn: shallowEqual },
  );

  useEffect(() => {
    if (unhydratedOutputs.length > 0) {
      kickoff(unhydratedOutputs.map((o) => o.id));
    }
  }, [kickoff, unhydratedOutputs]);
};

export const useCellData = ({
  appSessionCellId,
  filterErrors = false,
  /** By default, the loading state is debounced to avoid flickering. Sometimes this is not desireable, and can lead to more intermediate states. */
  skipDebounces = false,
}: UseCellDataArgs): {
  outputs: UseCellDataResult[];
  metadataOutputs: UseCellDataResult[];
  contentLoadStatus: OutputContentLoadStatus;
  state: CellExecutionState;
  id: string | undefined;
} => {
  const [isLoading, setIsLoading] = useState(false);

  const appSessionCell = useAppSessionCellSelector({
    appSessionCellId,
    selector: (cell) => {
      return {
        id: cell?.id,
        state: cell?.state,
      };
    },
    safe: true,
    equalityFn: shallowEqual,
  });

  const { kickoff } = useEnsureOutputLoaded();
  const { appSessionId } = useSessionContext();
  const selectByAppSessionCellId = useMemo(
    () =>
      appSessionCellId
        ? appSessionMPSelectors
            .getOutputSelectors(appSessionId)
            .createSelectByAppSessionCellIdWithResolvedContent(appSessionCellId)
        : () => stableEmptyArray<OutputMPWithResolvedContent>(),
    [appSessionCellId, appSessionId],
  );
  const cellOutputs = useSelector(
    selectByAppSessionCellId,
    // NOTE: Adding shallowEqual here ensures that changes to the referential
    // equality of selectByAppSessionCellId (e.g. when appSessionCellId updates
    // from undefined to defined) do not cause unnecessary renders.
    shallowEqual,
  );

  const cellContents = useMemo(
    () => cellOutputs.flatMap((o) => o.frontendOutputContents),
    [cellOutputs],
  );
  const unloadedOutputs = useMemo(
    () => [
      ...new Set(
        cellContents
          .filter((c) => c.status === OutputContentLoadStatus.NO_CONTENT)
          .map((c) => c.outputId),
      ),
    ],
    [cellContents],
  );

  const existingLoadingContents =
    unloadedOutputs.length > 0 ||
    cellContents.some((c) => c.status === OutputContentLoadStatus.LOADING);
  const existingFailedContents = cellContents.some(
    (c) => c.status === OutputContentLoadStatus.FAILED,
  );

  const cellDataToDebounce = useMemo(() => {
    return {
      ...getCellData({
        outputs: cellOutputs.filter((o) =>
          o.frontendOutputContents.every(
            (c) => c.status === OutputContentLoadStatus.READY,
          ),
        ),
        state: appSessionCell.state,
        filterErrors,
      }),
      id: appSessionCellId,
    };
  }, [appSessionCell.state, cellOutputs, filterErrors, appSessionCellId]);

  // When new outputs come in, cellOutputs is first cleared and _then_ filled.
  // This can lead to a flicker as we render new outputs, so we debounce it by 100ms
  // just to make things feel more smooth. Can be skipped to avoid race conditions.
  const [cellDataDebounced] = useDebounce(
    cellDataToDebounce,
    DEBOUNCE_RENDER_OUTPUT_STATE_MS,
  );

  const cellData = skipDebounces ? cellDataToDebounce : cellDataDebounced;

  useEffect(() => {
    if (unloadedOutputs.length > 0) {
      kickoff(unloadedOutputs);
    }
  }, [unloadedOutputs, kickoff]);

  // Intentionally using the debounced data here
  const hasOutputs = cellData.outputs.length > 0;
  useEffect(() => {
    if (
      existingLoadingContents &&
      !isLoading &&
      // If we have existing outputs, it looks better if we don't blank them out.
      // In that situation they'll "flicker" and change the cell height a couple of times.
      // But if there are no outputs, we do want to give the user the impression that something
      // is happening, especially if the cell is idle but we're still loading the output content.
      !hasOutputs
    ) {
      // We want to debounce this because some small outputs load so fast that it's more jarring to
      // show a loading wheel than it is to simply wait. Can be skipped to avoid race conditions.
      if (skipDebounces) {
        setIsLoading(true);
      } else {
        const t = setTimeout(() => {
          if (existingLoadingContents && !hasOutputs) {
            setIsLoading(true);
          }
        }, DEBOUNCE_OUTPUT_LOADING_STATE_MS);
        return () => {
          clearTimeout(t);
        };
      }
    } else if (!existingLoadingContents && isLoading) {
      setIsLoading(false);
    }
  }, [existingLoadingContents, isLoading, hasOutputs, skipDebounces]);

  return useMemo(() => {
    if (existingFailedContents) {
      return {
        ...cellData,
        contentLoadStatus: OutputContentLoadStatus.FAILED,
      };
    } else if (isLoading) {
      return {
        ...cellData,
        contentLoadStatus: OutputContentLoadStatus.LOADING,
      };
    } else {
      return {
        ...cellData,
        contentLoadStatus: OutputContentLoadStatus.READY,
      };
    }
  }, [existingFailedContents, isLoading, cellData]);
};

export function getCellData({
  filterErrors,
  outputs,
  state,
}: {
  outputs: OutputMPWithResolvedContent[] | undefined;
  state: AppSessionCellMP["state"] | undefined;
  filterErrors?: boolean;
}): {
  outputs: UseCellDataResult[];
  metadataOutputs: UseCellDataResult[];
  state: CellExecutionState;
} {
  const sortedOutputs = [...(outputs ?? [])]
    .filter(
      (output) =>
        (!filterErrors || output.outputType !== OutputType.ERROR) &&
        output.outputType !== OutputType.METADATA,
    )
    .sort((a, b) => a.order - b.order)
    .map((outputFragment, i) => ({
      key: i.toString(),
      outputFragment,
      type: parseOutputType(outputFragment),
    }));
  const filteredMetadataOutputs = [...(outputs ?? [])]
    .filter((output) => output.outputType === OutputType.METADATA)
    .map((outputFragment, i) => ({
      key: i.toString(),
      outputFragment,
      type: parseOutputType(outputFragment),
    }));
  const now = getDateTimeString(new Date());
  const totalOutputs =
    max(sortedOutputs.map((output) => output.outputFragment.order)) ?? 0;
  if (!filterErrors && totalOutputs > MAX_NON_METADATA_OUTPUT_COUNT) {
    const contents: OutputsTruncatedContents = {
      totalOutputs,
    };
    const outputId = uuid() as OutputId;
    sortedOutputs.unshift({
      key: "truncated",
      outputFragment: {
        __typename: "Output",
        exportTypesAndStatus: [],
        frontendOutputContents: [
          {
            __typename: "OutputContent",
            // We have to stringify twice because outputs are automatically deserialized, and we want output renderers to always accept strings
            contents: JSON.stringify(JSON.stringify(contents)),
            id: uuid() as OutputContentId,
            outputId,
            mimeType: OUTPUTS_TRUNCATED_MIMETYPE,
            createdDate: now,
            revision: 0,
            status: OutputContentLoadStatus.READY,
          },
        ],
        id: outputId,
        appSessionCellId: sortedOutputs[0].outputFragment.appSessionCellId,
        order: 1,
        revision: 0,
        requestId: null,
        outputType: OutputType.ERROR,
      },
      type: "string",
    });
  }

  return {
    outputs: sortedOutputs,
    metadataOutputs: filteredMetadataOutputs,
    state: state ?? CellExecutionState.STALE,
  };
}
